import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useHistory } from "react-router";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { capitalize, formatDate } from "helpers/Helpers";

import { PostInvoice, PutInvoice } from "api/rpc/2024-04/facilityAdmin/order/invoice";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Select } from "components/select/index";
import { ICustomer } from "redux/reducers/models/customer";
import { ITournament } from "redux/reducers/models/tournament";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useTranslation } from "react-i18next";
import Divider from "components/divider";
import Input from "components/form/input/Input";
import TextEditor from "components/textEditor/textEditor";
import { ButtonNew } from "components/buttonNew";
import "./invoice.scss";

interface IInvoiceState {
  customer_id: number;
  customer_billing_address_id: number;
  customer_shipping_address_id: number;
  date: Date;
  selected_event: any;
  invoice_due: string;
  register_id: number | null;
  po_number: string;
  notes: string;
}

interface IFilterState {
  eventQuery: string;
  selectedEvent: ITournament;
  eventSearching: boolean;
  customerQuery: string;
  selectedCustomer: ICustomer;
  customerSearching: boolean;
}

export default function InvoiceNew() {
  const { Option } = Select;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const facilityStore = useAppSelector(store => store.facilityStore);
  const [events, setEvents] = useState<Array<any>>(undefined);
  const [customers, setCustomers] = useState<Array<ICustomer>>([]);

  const [invoiceState, setInvoiceState] = useState<IInvoiceState>({
    customer_id: null,
    customer_billing_address_id: null,
    customer_shipping_address_id: null,
    date: new Date(),
    selected_event: null,
    invoice_due: null,
    register_id: null,
    po_number: "",
    notes: "",
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    eventQuery: "",
    selectedEvent: null,
    eventSearching: false,
    customerQuery: "",
    selectedCustomer: null,
    customerSearching: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadEvents(source.token);
    return () => {
      source.cancel();
    };
  }, [filterState.eventQuery]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomers(source.token);
    return () => {
      source.cancel();
    };
  }, [filterState.customerQuery]);

  async function loadEvents(token: CancelToken) {
    let tournaments: Array<any> = [];
    let events: Array<any> = [];
    setEvents(null);

    const tournamentRes = await GetTournament({ search: filterState.eventQuery }, false, token);

    if (token && token.reason) {
      return;
    }
    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.001")));
    } else {
      tournaments = tournamentRes.data.map(tournament => ({ ...tournament, event_type: "tournament" }));
    }

    const eventRes = await GetEvent({ search: filterState.eventQuery }, false, token);

    if (token && token.reason) {
      return;
    }
    if (eventRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.002")));
    } else {
      events = eventRes.data.map(event => ({ ...event, event_type: "hospitality" }));
    }

    const allEvents = tournaments.concat(events);

    setEvents(allEvents);
  }

  async function loadCustomers(token?: CancelToken) {
    setFilterState(prevState => ({ ...prevState, customerSearching: true }));
    setCustomers(undefined);

    const customerRes = await GetCustomer(
      {
        search: filterState.customerQuery,
        "address-lines": true,
      },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.003")));
      return;
    }

    setCustomers(customerRes.data);
    setFilterState(prevState => ({ ...prevState, customerSearching: false }));
  }

  async function createInvoice() {
    if (!invoiceState?.customer_id) {
      dispatch(showError(t("Please select a customer")));
      return;
    }

    const invoiceDate = formatDate(invoiceState?.date ?? new Date());
    const tournamentId =
      invoiceState?.selected_event?.event_type === "tournament" ? invoiceState?.selected_event?.id : null;
    const hospitalityEventId =
      invoiceState?.selected_event?.event_type === "hospitality" ? invoiceState?.selected_event?.id : null;
    const billingAddressId =
      invoiceState?.customer_billing_address_id === -1 ? null : invoiceState?.customer_billing_address_id;
    const shippingAddressId =
      invoiceState?.customer_shipping_address_id === -1 ? null : invoiceState?.customer_shipping_address_id;

    const invoiceRes = await PostInvoice(
      {
        customer_id: invoiceState?.customer_id,
        customer_billing_address_id: billingAddressId,
        customer_shipping_address_id: shippingAddressId,
        date: invoiceDate,
        tournament_id: tournamentId,
        hospitality_event_id: hospitalityEventId,
        invoice_due: invoiceState?.invoice_due,
        register_id: invoiceState?.register_id ?? undefined,
        po_number: invoiceState?.po_number,
        notes: invoiceState?.notes,
      },
      true,
    );

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.004")));
      return;
    }

    // Create may overwrite date with the event date; re-apply the Date field value.
    if (tournamentId || hospitalityEventId) {
      const updateRes = await PutInvoice(
        {
          invoice_id: invoiceRes.data.id,
          customer_id: invoiceState.customer_id,
          customer_billing_address_id: billingAddressId,
          customer_shipping_address_id: shippingAddressId,
          date: invoiceDate,
          tournament_id: tournamentId,
          hospitality_event_id: hospitalityEventId,
          invoice_due: invoiceState.invoice_due,
          register_id: invoiceState.register_id ?? undefined,
          po_number: invoiceState.po_number,
          notes: invoiceState.notes,
        },
        true,
      );

      if (updateRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.order.invoices.invoice_new.004")));
        return;
      }
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_new.005")));
    history.push(`/admin/invoices/${String(invoiceRes.data.id)}/edit`);
  }

  function handleEventChange(event?: any) {
    if (event) {
      setInvoiceState(prevState => ({ ...prevState, selected_event: event }));
      setFilterState(prevState => ({
        ...prevState,
        eventQuery: "",
        selectedEvent: event,
      }));
    }
  }

  function handleCustomerChange(value: any, id: string, customer?: ICustomer) {
    if (customer) {
      setInvoiceState(prevState => ({ ...prevState, [id]: customer.id === -1 ? null : customer.id }));
      setFilterState(prevState => ({ ...prevState, customerQuery: customer.full_name, selectedCustomer: customer }));
    }
  }

  function handleDatePickerChange(selectedDate: Date) {
    setInvoiceState(prevState => ({ ...prevState, date: selectedDate }));
  }

  function handleDropDownChange(value: any, property: string) {
    setInvoiceState(prev => ({ ...prev, [property]: value }));
  }

  function handleUpdateNoteMarkdown(markdown: string) {
    setInvoiceState(prev => ({ ...prev, notes: markdown }));
  }

  const primaryAction = {
    content: t("secure.facility.order.invoices.invoice_new.006"),
    action: createInvoice,
  };

  return (
    <Page
      title={t("secure.facility.order.invoices.invoice_new.007")}
      primaryAction={primaryAction}
      breadcrumbs={[
        { prefix: true, label: t("secure.facility.order.invoices.invoice_new.008"), url: "/admin/invoices" },
      ]}
    >
      <Card>
        <Card.Section title={t("secure.facility.order.invoices.invoice_new.009")}>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <DatePickerInput
                  label={t("secure.facility.order.invoices.invoice_new.010")}
                  months={1}
                  position="left"
                  startingDate={invoiceState.date}
                  setStartingDate={handleDatePickerChange}
                />
                <Select
                  label={t("secure.facility.order.invoices.invoice_new.011")}
                  onChange={(value: string) => setInvoiceState(prevState => ({ ...prevState, invoice_due: value }))}
                >
                  <Option key={0} value={"on_receipt"}>
                    {t("secure.facility.order.invoices.invoice_new.012")}
                  </Option>
                  <Option key={1} value={"net_30"}>
                    {t("secure.facility.order.invoices.invoice_new.013")}
                  </Option>
                  <Option key={2} value={"net_60"}>
                    {t("secure.facility.order.invoices.invoice_new.014")}
                  </Option>
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                {invoiceState.selected_event ? (
                  <div>
                    <div>
                      <p className="event-label">{t("secure.facility.order.invoices.invoice_new.015")}</p>
                    </div>

                    <div className="selected-container">
                      <div className="event-name">
                        <div>{invoiceState.selected_event.name}</div>

                        <div>
                          <button
                            className=""
                            onClick={() => setInvoiceState(prevState => ({ ...prevState, selected_event: null }))}
                          >
                            <Icon icon={faTimes} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Select
                    showSearch
                    label={t("secure.facility.order.invoices.invoice_new.016")}
                    onSearch={(query: string) => setFilterState(prevState => ({ ...prevState, eventQuery: query }))}
                    onChange={(value: any, event: any) => handleEventChange(event)}
                    placeholder={t("secure.facility.order.invoices.invoice_new.017")}
                    allowClear
                    searchValue={filterState.eventQuery}
                    showDropDownOnFocus={true}
                    searching={!events}
                  >
                    <Option
                      value={-1}
                      extraValues={{ name: t("secure.facility.order.invoices.invoice_new.018"), id: -1 }}
                    >
                      <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_new.019")}</div>
                    </Option>
                    {events?.map((event: any, index: number) => {
                      return (
                        <Option value={event?.id} extraValues={event} key={index}>
                          {`${String(event.name)} (${String(capitalize(String(event.event_type)))}`}
                        </Option>
                      );
                    })}
                  </Select>
                )}
                <Select
                  label={t("Register")}
                  onChange={(value: number) => setInvoiceState(prevState => ({ ...prevState, register_id: value }))}
                  defaultValue={invoiceState.register_id ?? null}
                >
                  {facilityStore?.registers?.map((register, registerIndex) => (
                    <Option key={registerIndex} value={register.id} name={register.title}>
                      {register.title}
                    </Option>
                  ))}
                </Select>
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
      </Card>
      <Card
        title={t("Bill to")}
        subtitle={t("Search for the customer this invoice is addressed to. Addresses will auto-fill once selected.")}
      >
        <Divider className="mt-4! mb-4!" />
        <div className="-mt-4 invoice-new-bill-to">
          <Card.Section>
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Select
                    showSearch
                    label={t("secure.facility.order.invoices.invoice_new.021")}
                    onSearch={(query: string) => setFilterState(prevState => ({ ...prevState, customerQuery: query }))}
                    onChange={(value: any, customer: ICustomer) => handleCustomerChange(value, "customer_id", customer)}
                    placeholder={t("Search a customer by name or email address...")}
                    searchValue={filterState.customerQuery}
                    showDropDownOnFocus={true}
                    searching={filterState.customerSearching}
                  >
                    {customers?.map((customer: ICustomer, index: number) => {
                      return (
                        <Option value={customer?.id} extraValues={customer} key={index}>
                          <div className="invoice-new-bill-to__customer-option">
                            <div className="text-semibold text-lg">{customer.full_name}</div>
                            <div className="text-sm">{customer.email}</div>
                          </div>
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <Select
                    label={t("secure.facility.order.invoices.invoice_new.023")}
                    searchValue=""
                    onChange={(value: any) => handleDropDownChange(value, "customer_billing_address_id")}
                    showDropDownOnFocus={true}
                    disabled={!invoiceState.customer_id}
                  >
                    <Option
                      value={-1}
                      extraValues={{ name: t("secure.facility.order.invoices.invoice_new.024"), id: -1 }}
                    >
                      <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_new.025")}</div>
                    </Option>
                    {filterState.selectedCustomer?.address_lines?.map((address: any, index: number) => {
                      return (
                        <Option value={address?.id} key={index}>
                          <div className="text-semibold text-lg">
                            {address.address_line_1}
                            <span className="text-sm">{` ${String(address.city)}, ${String(
                              address.province_name,
                            )}`}</span>
                          </div>
                        </Option>
                      );
                    })}
                  </Select>
                  <Select
                    label={t("secure.facility.order.invoices.invoice_new.026")}
                    onChange={(value: any) => handleDropDownChange(value, "customer_shipping_address_id")}
                    showDropDownOnFocus={true}
                    disabled={!invoiceState.customer_id}
                  >
                    <Option
                      value={-1}
                      extraValues={{ name: t("secure.facility.order.invoices.invoice_new.027"), id: -1 }}
                    >
                      <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_new.028")}</div>
                    </Option>
                    {filterState.selectedCustomer?.address_lines?.map((address: any, index: number) => {
                      return (
                        <Option value={address?.id} key={index}>
                          <div className="text-semibold text-lg">
                            {address.address_line_1}
                            <span className="text-sm">{` ${String(address.city)}, ${String(
                              address.province_name,
                            )}`}</span>
                          </div>
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
              </FormLayout>
            </Form>
          </Card.Section>
        </div>
      </Card>

      <Card title={t("Additional Details")} subtitle={t("Both fields are optional. Leave blank if not needed.")}>
        <Divider className="mt-4! mb-4!" />
        <div className="-mt-4">
          {/* PO Box + Notes */}
          <Card.Section>
            <div className="pb-4">
              <Input
                value={invoiceState?.po_number}
                label={t("PO Number")}
                id={"po_number"}
                onChange={e => setInvoiceState(prev => ({ ...prev, po_number: e.target.value }))}
                helpText={t("Enter the purchase order number for the customer's record.")}
              />
            </div>

            <div className="pb-2">
              <TextEditor
                label={t("Notes")}
                markdownText={invoiceState?.notes}
                markdownTextOnChange={handleUpdateNoteMarkdown}
              />
            </div>
            <ButtonNew size="small" onClick={() => setInvoiceState(prev => ({ ...prev, notes: "" }))}>
              {t("Clear Content")}
            </ButtonNew>
          </Card.Section>
        </div>
      </Card>
    </Page>
  );
}
